/**
 * Lidský název produktu vs. ceníkové / ERP stuby (`SAP 325702`).
 *
 * Stuby historicky vznikaly u importu dodavatelského ceníku (`SAP {code}` / Nezařazené);
 * nové ceníky už produkty nezakládají. AutoERP `ensureProductByCode` pořád používá `name = "SAP {code}"`.
 */

/** Titulek, když v katalogu/skladu není skutečný název — SAP patří do podtitulku. */
export const UNASSIGNED_PRODUCT_LABEL_CS = 'Nezařazený produkt';

function normalizeSapCode(sapCode: string | null | undefined): string {
  const sap = String(sapCode ?? '').trim();
  if (!sap || sap === '—') return '';
  return sap;
}

/**
 * Placeholder název: prázdný, holý SAP kód, nebo `SAP {code}` / `SAP-code`.
 * Skutečný název začínající na „SAP “ (např. „SAP Patchcord …“) není placeholder.
 */
export function isSapPlaceholderProductName(
  name: string | null | undefined,
  sapCode?: string | null,
): boolean {
  const n = String(name ?? '').trim();
  if (!n) return true;
  const sap = normalizeSapCode(sapCode);
  if (sap && n.toLowerCase() === sap.toLowerCase()) return true;
  const m = /^SAP[\s:_-]*(.+)$/i.exec(n);
  if (!m) return false;
  const rest = m[1].trim();
  if (!sap) return true;
  return rest.toLowerCase() === sap.toLowerCase();
}

/** První ne-stub název v pořadí preferencí (katalog → sklad → …). */
export function firstRealProductName(
  names: readonly (string | null | undefined)[],
  sapCode?: string | null,
): string | null {
  for (const raw of names) {
    const n = String(raw ?? '').trim();
    if (n && !isSapPlaceholderProductName(n, sapCode)) return n;
  }
  return null;
}

/**
 * Titulek produktu pro UI. Nikdy nevrací `SAP {kód}` jako jméno, když existuje
 * lepší zdroj; jinak „Nezařazený produkt“ (SAP zobrazte zvlášť).
 */
export function resolveProductDisplayName(args: {
  sapCode?: string | null;
  names?: readonly (string | null | undefined)[];
  fallback?: string;
}): string {
  const real = firstRealProductName(args.names ?? [], args.sapCode);
  if (real) return real;
  const fallback = String(args.fallback ?? '').trim();
  return fallback || UNASSIGNED_PRODUCT_LABEL_CS;
}

/** Název řádku katalogu (`zdrojovyNazev`) se sap-stub fallbackem. */
export function catalogItemDisplayName(
  item: { sap?: string | null; zdrojovyNazev?: string | null } | null | undefined,
  extraNames?: readonly (string | null | undefined)[],
): string {
  return resolveProductDisplayName({
    sapCode: item?.sap,
    names: [item?.zdrojovyNazev, ...(extraNames ?? [])],
  });
}
